/**
 * Catalog API
 * Provides methods for work with objects of catalog
 * - Groups of categories (like Countries, Languages, Sections)
 * - Publications
 * - Locales
 *
 * Singleton per application.
 */

import { ICatalogGroupDto } from '@pressreader/catalog-nav-groups-api';
import { IPublicationDto, IPublicationsQuery } from '@pressreader/catalog-publications';
import { ICatalogLocale } from '@pressreader/catalog-types';
import { IPagedResult } from '@pressreader/types';

import CatalogService from './catalog.service';

// Created lazily so importing the lib has no side effects (`sideEffects: false`). Deferring the
// `svcAuth` binding and the catalog-version poll to first use cannot create a staleness window:
// caches are written only by instance methods, and any method call constructs the instance first,
// wiring both invalidation paths in the constructor — strictly before the first cache write. A
// first call after any idle period hits empty caches and fetches fresh; the poll only has an
// observable effect once something is cached, and it is always active by then.
let instance: CatalogService | undefined;

function getCatalog(): CatalogService {
    instance ??= CatalogService.create();
    return instance;
}

export const getPublication = (cid: string): Promise<IPublicationDto> => getCatalog().getPublication(cid);
export const getPublications = (cids: string[]): Promise<IPublicationDto[]> => getCatalog().getPublications(cids);
export const findPublications = (query: IPublicationsQuery): Promise<IPagedResult<IPublicationDto>> => getCatalog().findPublications(query);
export const getLanguages = (): Promise<ICatalogGroupDto[]> => getCatalog().getLanguages();
export const getCountries = (): Promise<ICatalogGroupDto[]> => getCatalog().getCountries();
export const getCategories = (): Promise<ICatalogGroupDto[]> => getCatalog().getCategories();
export const getLocales = (): Promise<ICatalogLocale[]> => getCatalog().getLocales();

/**
 * Shared singleton facade for consumers that inject a catalog object (e.g. the publication
 * select dialog). Use this rather than constructing a `CatalogService` — each instance starts
 * its own metadata poll timer + `svcAuth` binding and keeps separate caches. Deliberately
 * excludes `destroy()`: the app-wide singleton must not be torn down by a consumer.
 */
export const catalogService = {
    getPublication,
    getPublications,
    findPublications,
    getLanguages,
    getCountries,
    getCategories,
    getLocales,
};
